/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { useCallback, useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import { randomId } from '../../../../utils'
import {
  addAfterSmoothScrollManagerRender,
  removeAfterSmoothScrollManagerRender,
} from '../../../Background/init'

interface StickyContainerProps {
  stickies: HTMLElement[]
}

export interface Range {
  key: string
  el: HTMLElement
  startAt: number
  endAt: number
  offsetY: number
  top: number
  left: number
  height: number
  width: number
  rootRect?: any
  rect?: any
  totalHeight?: any
}

function dealRange(y: number, range: Range): void {
  const { el, startAt, endAt } = range
  if (y < startAt) {
    el.style.transform = 'none'
  } else if (y < endAt) {
    el.style.transform = `translate(0, ${y - startAt}px)`
  } else {
    el.style.transform = `translate(0, ${endAt}px)`
  }
}

const StickyContainer: React.FC<StickyContainerProps> = ({
  stickies,
  children,
}) => {
  const container = useRef<HTMLDivElement>(null)
  const [ranges, setRanges] = useState<Range[]>([])

  const updateLimits = useCallback(() => {
    const rootRect = container.current!.getBoundingClientRect()

    const stFiltered = stickies.filter((st) => st)
    const stdata = stFiltered.map((st) => {
      const rect = st.getBoundingClientRect()
      return [rect.top + window.scrollY, rect.height, rect.width, rect.left]
    })
    const heightsums = [0]
    for (let i = 1; i < stdata.length; i++) {
      const [, height] = stdata[i - 1]
      heightsums.push(heightsums[i - 1] + height)
    }
    const totalHeight = stdata.map((st) => st[1]).reduce((a, b) => a + b, 0)
    setRanges(
      stdata.map((st, i) => {
        const [top, height, width, left] = st
        return {
          key: randomId(),
          el: stFiltered[i],
          startAt: top - heightsums[i],
          offsetY: heightsums[i],
          endAt: rootRect.top + rootRect.height - totalHeight,
          top,
          height,
          width,
          left,
        }
      })
    )
  }, [stickies])

  useEffect(() => {
    updateLimits()
  }, [stickies])

  useEffect(() => {
    const hook = (): void => {
      ranges.forEach((range) => {
        dealRange(window.scrollY, range)
      })
    }
    window.addEventListener('scroll', hook, false)
    return () => {
      window.removeEventListener('scroll', hook)
    }
  }, [ranges])

  return <div ref={container}>{children}</div>
}

export function calculateStickyLimit(
  $container: HTMLElement,
  stickies: HTMLElement[]
): Array<Range | null> {
  const rootRect = $container.getBoundingClientRect()
  if (rootRect.height < window.innerHeight) return stickies.map(() => null)

  const stFiltered = stickies.filter((st) => st)
  const stRects = stFiltered.map((st) => st.getBoundingClientRect())
  const heightsums = [0]
  for (let i = 1; i < stRects.length; i++) {
    const { height } = stRects[i - 1]
    heightsums.push(heightsums[i - 1] + height)
  }
  const totalHeight = stRects.map((st) => st.height).reduce((a, b) => a + b, 0)

  const ret = stickies.map((st, i) => {
    if (!st) return null
    const rect = st.getBoundingClientRect()
    const { top, height, width, left } = rect
    if (height * width === 0) return null
    return {
      key: randomId(),
      el: stFiltered[i],
      startAt: top - heightsums[i],
      offsetY: heightsums[i],
      endAt: rootRect.top + rootRect.height - window.innerHeight,
      top,
      height,
      width,
      left,
      rootRect,
      rect,
      totalHeight,
    }
  })
  return ret
}

interface StickyReplacementProps {
  range: Range
}
const StickyReplacement: React.FC<StickyReplacementProps> = ({ range }) => {
  return (
    <div
      className="sticky-replacement"
      style={{ width: range.width, paddingBottom: range.height }}
    ></div>
  )
}

const $body = document.body

interface StickyPortalProps {
  range: Range
  zIndex?: number
}

class StickyPortal extends React.Component<StickyPortalProps> {
  render(): React.ReactNode {
    const { range, children, zIndex } = this.props
    return ReactDOM.createPortal(
      <div
        style={{
          position: 'fixed',
          top: range.offsetY,
          left: range.left,
          width: range.width,
          zIndex: zIndex ?? 100,
        }}
        className="no-effect"
      >
        {children}
      </div>,
      $body
    )
  }
}

interface StickyProps {
  range?: Range | null
  el: HTMLElement | null
  zIndex?: number
  debug?: boolean
}

type StickyState = 'normal' | 'fixed' | 'over'

const Sticky: React.FC<StickyProps> = ({
  children,
  range,
  el,
  zIndex,
  debug = false,
}) => {
  const [state, setState] = useState<StickyState>('normal')

  useEffect(() => {
    const id = addAfterSmoothScrollManagerRender((velocityY: number) => {
      if (!el || !range) return
      const { startAt, endAt } = range
      const y = -velocityY
      // const y = window.scrollY
      if (y < startAt) {
        setState('normal')
      } else if (y < endAt) {
        setState('fixed')
      } else {
        setState('over')
      }
    })
    return () => {
      removeAfterSmoothScrollManagerRender(id)
    }
  }, [el, range, debug])

  useEffect(() => {
    if (!el || !range) return

    const { startAt, endAt } = range
    if (state === 'over') {
      el.style.transform = `translate(0, ${endAt - startAt}px)`
    } else {
      el.style.transform = 'none'
    }
  }, [state, el, range])

  if (!range || !el || state === 'normal') {
    return <>{children}</>
  }

  if (state === 'fixed') {
    return (
      <>
        <StickyReplacement range={range} />
        <StickyPortal zIndex={zIndex} range={range}>
          {children}
        </StickyPortal>
      </>
    )
  }

  return <>{children}</>
}

export { StickyContainer, Sticky }
